/**
 * AYU Hilfe & Support
 * 
 * Tabs:
 * 1. Chat — FAQ-Chatbot (eingebettet, App-Modus)
 * 2. FAQ — Häufige Fragen (statisch + aus sharedConfig)
 * 3. Kontakt — Support-Infos + Notfall
 */
import { useState } from "react";
import { useClientAuth } from "@/contexts/ClientAuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { sharedConfig } from "@/config/clientConfig";
import FaqChatWidget from "@/components/FaqChatWidget";
import {
  MessageCircle,
  BookOpen,
  Mail,
  Phone,
  ChevronDown,
  ChevronUp,
  Compass,
  Headphones,
  FileText,
  Users,
  Sparkles,
  Shield,
  HelpCircle,
} from "lucide-react";

interface FaqItem {
  question: string;
  answer: string;
  icon: React.ReactNode;
}

export default function AppHilfe() {
  const { client } = useClientAuth();
  const { t } = useI18n();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<"chat" | "faq" | "kontakt">("chat");

  // Combine built-in FAQ + sharedConfig FAQ
  const allFaqItems: FaqItem[] = [
    { icon: <Sparkles className="w-4 h-4" />, question: t("hilfe.faqWieStarten"), answer: t("hilfe.faqWieStartenAntwort") },
    { icon: <Compass className="w-4 h-4" />, question: t("hilfe.faqPhenotyp"), answer: t("hilfe.faqPhenotypAntwort") },
    { icon: <MessageCircle className="w-4 h-4" />, question: t("hilfe.faqAnnam"), answer: t("hilfe.faqAnnamAntwort") },
    { icon: <BookOpen className="w-4 h-4" />, question: t("hilfe.faqTagebuch"), answer: t("hilfe.faqTagebuchAntwort") },
    { icon: <Headphones className="w-4 h-4" />, question: t("hilfe.faqSessions"), answer: t("hilfe.faqSessionsAntwort") },
    { icon: <FileText className="w-4 h-4" />, question: t("hilfe.faqDokumente"), answer: t("hilfe.faqDokumenteAntwort") },
    { icon: <Users className="w-4 h-4" />, question: t("hilfe.faqCommunity"), answer: t("hilfe.faqCommunityAntwort") },
    { icon: <Shield className="w-4 h-4" />, question: t("hilfe.faqDaten"), answer: t("hilfe.faqDatenAntwort") },
    ...sharedConfig.faq.map((item) => ({
      question: item.q,
      answer: item.a,
      icon: <HelpCircle className="w-4 h-4" />,
    })),
  ];

  return (
    <div className="h-full flex flex-col px-4 pt-4 pb-2 overflow-hidden">
      {/* Header */}
      <div className="mb-4 shrink-0">
        <h1 className="text-xl font-bold text-[#2d3a1a] font-['Cormorant_Garamond']">
          {t("hilfe.title")}
        </h1>
        <p className="text-sm text-[#6b7c5a] mt-1">
          {t("hilfe.subtitle")}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 shrink-0 bg-[#f5f0e8] rounded-lg p-1">
        <button
          onClick={() => setActiveTab("chat")}
          className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === "chat"
              ? "bg-white text-[#2d3a1a] shadow-sm"
              : "text-[#6b7c5a] hover:text-[#2d3a1a]"
          }`}
        >
          <MessageCircle className="w-4 h-4" />
          {t("hilfe.chatTab")}
        </button>
        <button
          onClick={() => setActiveTab("faq")}
          className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === "faq"
              ? "bg-white text-[#2d3a1a] shadow-sm"
              : "text-[#6b7c5a] hover:text-[#2d3a1a]"
          }`}
        >
          <BookOpen className="w-4 h-4" />
          {t("hilfe.faqTab")}
        </button>
        <button
          onClick={() => setActiveTab("kontakt")}
          className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === "kontakt"
              ? "bg-white text-[#2d3a1a] shadow-sm"
              : "text-[#6b7c5a] hover:text-[#2d3a1a]"
          }`}
        >
          <Mail className="w-4 h-4" />
          {t("hilfe.kontaktTab")}
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {/* Chat Tab */}
        {activeTab === "chat" && (
          <div className="h-full">
            <FaqChatWidget
              mode="app"
              clientId={client?.clientId}
              clientName={client?.firstName || undefined}
            />
          </div>
        )}

        {/* FAQ Tab */}
        {activeTab === "faq" && (
          <div className="h-full overflow-y-auto space-y-2 pb-4">
            {/* How to use AYU */}
            <div className="bg-white rounded-xl border border-[#e8e0d4] overflow-hidden mb-4">
              <div className="px-4 py-3 border-b border-[#e8e0d4] flex items-center gap-2 bg-[#faf8f4]">
                <BookOpen className="w-4 h-4 text-[#5a6b3c]" />
                <h2 className="text-sm font-semibold text-[#2d3a1a]">{t("hilfe.soNutzt")}</h2>
              </div>
              <div className="p-4 space-y-3">
                {[
                  { num: "1", title: t("hilfe.step1Title"), desc: t("hilfe.step1Desc") },
                  { num: "2", title: t("hilfe.step2Title"), desc: t("hilfe.step2Desc") },
                  { num: "3", title: t("hilfe.step3Title"), desc: t("hilfe.step3Desc") },
                ].map((step) => (
                  <div key={step.num} className="flex gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#5a6b3c]/10 flex items-center justify-center shrink-0 mt-0.5">
                      <span className="text-xs font-semibold text-[#5a6b3c]">{step.num}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[#2d3a1a]">{step.title}</p>
                      <p className="text-xs text-[#6b7c5a] mt-0.5">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* FAQ Items */}
            {allFaqItems.map((item, index) => (
              <div
                key={index}
                className="border border-[#e8e0d4] rounded-xl overflow-hidden bg-white"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#faf8f4] transition-colors"
                >
                  <div className="w-8 h-8 rounded-lg bg-[#f5f0e8] flex items-center justify-center shrink-0 text-[#5a6b3c]">
                    {item.icon}
                  </div>
                  <span className="flex-1 text-sm font-medium text-[#2d3a1a]">
                    {item.question}
                  </span>
                  {openFaq === index ? (
                    <ChevronUp className="w-4 h-4 text-[#6b7c5a] shrink-0" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-[#6b7c5a] shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-4 pb-3 pt-0 ml-11">
                    <p className="text-sm text-[#6b7c5a] leading-relaxed">{item.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Kontakt Tab */}
        {activeTab === "kontakt" && (
          <div className="h-full overflow-y-auto space-y-4 pb-4">
            <div className="bg-white rounded-xl border border-[#e8e0d4] p-5">
              <h3 className="font-semibold text-[#2d3a1a] mb-3">{t("hilfe.kontaktTitle")}</h3>
              <p className="text-sm text-[#6b7c5a] mb-4">
                {t("hilfe.kontaktSubtitle")}
              </p>
              <div className="space-y-3">
                <a
                  href={`mailto:${sharedConfig.supportEmail}`}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[#f5f0e8] hover:bg-[#ede6d8] transition-colors"
                >
                  <Mail className="w-5 h-5 text-[#5a6b3c]" />
                  <div>
                    <p className="text-sm font-medium text-[#2d3a1a]">{t("hilfe.emailLabel")}</p>
                    <p className="text-xs text-[#6b7c5a]">{sharedConfig.supportEmail}</p>
                  </div>
                </a>
                <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[#f5f0e8]">
                  <Phone className="w-5 h-5 text-[#5a6b3c]" />
                  <div>
                    <p className="text-sm font-medium text-[#2d3a1a]">{t("hilfe.telefonLabel")}</p>
                    <p className="text-xs text-[#6b7c5a]">{t("hilfe.telefonZeiten")}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#5a6b3c] to-[#4a5b2c] rounded-xl p-5 text-white">
              <h3 className="font-semibold mb-2">{t("hilfe.schnelleHilfe")}</h3>
              <p className="text-sm text-white/80 mb-3">
                {t("hilfe.schnelleHilfeDesc")}
              </p>
              <button
                onClick={() => setActiveTab("chat")}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors text-sm font-medium"
              >
                <MessageCircle className="w-4 h-4" />
                {t("hilfe.zumChat")}
              </button>
            </div>

            {/* Emergency */}
            <div className="bg-red-50/50 rounded-xl border border-red-100 p-5 text-center">
              <Phone className="w-5 h-5 text-red-300 mx-auto mb-2" />
              <p className="text-sm text-red-400 font-medium">{t("hilfe.akuteKrise")}</p>
              <p className="text-xs text-red-300 mt-1">
                {sharedConfig.emergencyNote}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
